<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import AiAPI from 'custom/api/ai';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';
import { useRoute } from 'vue-router';
import { useAiSettings } from './useAiSettings';
import { SOURCE_ICONS, isSyncing, learnedFromRoute } from './helpers';
import AssistantLearning from './AssistantLearning.vue';

// Assistant › Knowledge: web pages and sitemaps, files, texts, Q&A pairs, the help center, and Q&A learned from the
// team's conversations (suggested until approved). Sources sync in the background; the list refreshes while any is
// syncing or conversations are being read.
const props = defineProps({
  assistant: { type: Object, required: true },
});
const emit = defineEmits(['counts', 'updated']);

const { t, locale } = useLocoI18n();
const { settings, load } = useAiSettings();

const KINDS = ['url', 'file', 'text', 'faq', 'help_center'];
const POLL_MS = 3000;
// How long to keep refreshing after asking to read past conversations
const LEARNING_POLL_MS = 120000;

const route = useRoute();

const sources = ref([]);
const kind = ref('url');
const draft = reactive({ url: '', title: '', content: '', sitemap: false });
const file = ref(null);
const fileInput = ref(null);
const isAdding = ref(false);
const editing = ref(null);
const editDialogRef = ref(null);
let pollTimer = null;
let learningUntil = 0;

const knowledge = computed(() =>
  sources.value.filter(source => source.status !== 'suggested')
);
const suggestions = computed(() =>
  sources.value.filter(source => source.status === 'suggested')
);
const syncing = computed(() => sources.value.some(isSyncing));
const keywordOnly = computed(
  () => settings.value && !settings.value.embeddings_configured
);
const canAdd = computed(() => {
  if (kind.value === 'url') return /^https?:\/\/\S+/.test(draft.url);
  if (kind.value === 'file') return Boolean(file.value);
  if (kind.value === 'text') return Boolean(draft.content.trim());
  if (kind.value === 'faq')
    return Boolean(draft.title.trim() && draft.content.trim());
  return true;
});

const sourceName = source =>
  source.title ||
  source.url ||
  source.filename ||
  t(`LOCO.AI.KNOWLEDGE.KINDS.${source.kind.toUpperCase()}`);

const syncedAt = source =>
  source.synced_at
    ? new Date(source.synced_at * 1000).toLocaleString(intlLocale(locale.value))
    : '';

const errorMessage = error => error.response?.data?.error || error.message;

const emitCounts = () =>
  emit('counts', {
    sources_count: knowledge.value.length,
    suggestions_count: suggestions.value.length,
  });

const fetchSources = async () => {
  const { data } = await AiAPI.sources(props.assistant.id);
  sources.value = data.payload;
  emitCounts();
};

const poll = () => {
  clearTimeout(pollTimer);
  if (!syncing.value && Date.now() > learningUntil) return;
  pollTimer = setTimeout(async () => {
    await fetchSources();
    poll();
  }, POLL_MS);
};

const reset = () => {
  Object.assign(draft, { url: '', title: '', content: '', sitemap: false });
  file.value = null;
  if (fileInput.value) fileInput.value.value = '';
};

const payload = () => {
  if (kind.value === 'file') {
    const data = new FormData();
    data.append('kind', 'file');
    data.append('file', file.value);
    if (draft.title.trim()) data.append('title', draft.title.trim());
    return data;
  }
  return {
    kind: kind.value,
    url: kind.value === 'url' ? draft.url.trim() : undefined,
    title: draft.title.trim() || undefined,
    content: draft.content || undefined,
    settings: draft.sitemap ? { crawl: 'sitemap' } : undefined,
  };
};

const add = async () => {
  isAdding.value = true;
  try {
    const { data } = await AiAPI.createSource(props.assistant.id, payload());
    sources.value = [data, ...sources.value];
    emitCounts();
    reset();
    poll();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isAdding.value = false;
  }
};

const replace = data => {
  sources.value = sources.value.map(source =>
    source.id === data.id ? data : source
  );
};

const sync = async source => {
  try {
    const { data } = await AiAPI.syncSource(props.assistant.id, source.id);
    replace(data);
    poll();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const remove = async source => {
  try {
    await AiAPI.deleteSource(props.assistant.id, source.id);
    sources.value = sources.value.filter(item => item.id !== source.id);
    emitCounts();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const approve = async source => {
  try {
    const { data } = await AiAPI.approveSource(props.assistant.id, source.id);
    replace(data);
    emitCounts();
    useAlert(t('LOCO.AI.LEARN.APPROVED'));
    poll();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const startLearningPoll = () => {
  learningUntil = Date.now() + LEARNING_POLL_MS;
  poll();
};

const startEdit = source => {
  editing.value = { ...source };
  editDialogRef.value.open();
};

const saveEdit = async () => {
  try {
    const { data } = await AiAPI.updateSource(
      props.assistant.id,
      editing.value.id,
      { title: editing.value.title, content: editing.value.content }
    );
    replace(data);
    editDialogRef.value.close();
    poll();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const onFile = event => {
  [file.value] = event.target.files;
};

onMounted(async () => {
  load();
  await fetchSources();
  poll();
});
onBeforeUnmount(() => clearTimeout(pollTimer));
</script>

<template>
  <div class="flex flex-col gap-6 pb-8">
    <Banner v-if="keywordOnly" color="slate">
      {{ t('LOCO.AI.KNOWLEDGE.KEYWORD_ONLY') }}
    </Banner>

    <AssistantLearning
      :assistant="assistant"
      :suggestions="suggestions"
      @updated="data => emit('updated', data)"
      @learning="startLearningPoll"
      @approve="approve"
      @edit="startEdit"
      @dismiss="remove"
    />

    <form
      class="flex flex-col gap-4 p-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
      @submit.prevent="add"
    >
      <div class="flex flex-wrap gap-1">
        <button
          v-for="item in KINDS"
          :key="item"
          type="button"
          class="flex items-center gap-1.5 h-8 px-3 rounded-lg text-sm"
          :class="
            kind === item
              ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="kind = item"
        >
          <Icon :icon="SOURCE_ICONS[item]" class="size-4" />
          {{ t(`LOCO.AI.KNOWLEDGE.KINDS.${item.toUpperCase()}`) }}
        </button>
      </div>

      <p class="text-sm text-n-slate-11">
        {{ t(`LOCO.AI.KNOWLEDGE.HELP.${kind.toUpperCase()}`) }}
      </p>

      <template v-if="kind === 'url'">
        <input
          v-model="draft.url"
          type="url"
          class="!mb-0"
          :placeholder="t('LOCO.AI.KNOWLEDGE.URL_PLACEHOLDER')"
        />
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="draft.sitemap" type="checkbox" />
          {{ t('LOCO.AI.KNOWLEDGE.SITEMAP') }}
        </label>
      </template>

      <template v-else-if="kind === 'file'">
        <input
          ref="fileInput"
          type="file"
          accept=".pdf,.txt,.md,.csv,.html,.htm,application/pdf,text/*"
          class="!mb-0 text-sm"
          @change="onFile"
        />
        <input
          v-model="draft.title"
          type="text"
          class="!mb-0"
          :placeholder="t('LOCO.AI.KNOWLEDGE.TITLE_OPTIONAL')"
        />
      </template>

      <template v-else-if="kind === 'text'">
        <input
          v-model="draft.title"
          type="text"
          class="!mb-0"
          :placeholder="t('LOCO.AI.KNOWLEDGE.TITLE_OPTIONAL')"
        />
        <textarea
          v-model="draft.content"
          rows="6"
          class="!mb-0 resize-y"
          :placeholder="t('LOCO.AI.KNOWLEDGE.TEXT_PLACEHOLDER')"
        />
      </template>

      <template v-else-if="kind === 'faq'">
        <input
          v-model="draft.title"
          type="text"
          class="!mb-0"
          :placeholder="t('LOCO.AI.KNOWLEDGE.QUESTION')"
        />
        <textarea
          v-model="draft.content"
          rows="3"
          class="!mb-0 resize-y"
          :placeholder="t('LOCO.AI.KNOWLEDGE.ANSWER')"
        />
      </template>

      <div>
        <Button
          type="submit"
          sm
          icon="i-lucide-plus"
          :label="t('LOCO.AI.KNOWLEDGE.ADD')"
          :disabled="!canAdd"
          :is-loading="isAdding"
        />
      </div>
    </form>

    <p v-if="!knowledge.length" class="text-sm text-n-slate-11">
      {{ t('LOCO.AI.KNOWLEDGE.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col divide-y divide-n-weak">
      <li
        v-for="source in knowledge"
        :key="source.id"
        class="flex items-start gap-3 py-3"
      >
        <Icon
          :icon="SOURCE_ICONS[source.kind]"
          class="size-4 mt-0.5 flex-shrink-0 text-n-slate-11"
        />
        <div class="flex flex-col gap-0.5 min-w-0 flex-1">
          <span class="text-sm text-n-slate-12 truncate">
            {{ sourceName(source) }}
          </span>
          <span
            v-if="source.url && source.title"
            class="text-xs text-n-slate-10 truncate"
          >
            {{ source.url }}
          </span>
          <router-link
            v-if="source.settings?.conversation_display_id"
            :to="learnedFromRoute(route.params.accountId, source)"
            class="self-start text-xs text-n-blue-11 hover:underline"
          >
            {{
              t('LOCO.AI.LEARN.FROM', {
                id: source.settings.conversation_display_id,
              })
            }}
          </router-link>
          <span class="flex flex-wrap items-center gap-2 text-xs">
            <span
              v-if="isSyncing(source)"
              class="flex items-center gap-1 text-n-blue-11"
            >
              <span class="i-lucide-loader-circle size-3 animate-spin" />
              {{ t('LOCO.AI.KNOWLEDGE.SYNCING') }}
            </span>
            <span v-else-if="source.status === 'failed'" class="text-n-ruby-11">
              {{ t('LOCO.AI.KNOWLEDGE.FAILED', { error: source.error }) }}
            </span>
            <span v-else class="text-n-teal-11">
              {{
                t(
                  'LOCO.AI.KNOWLEDGE.READY',
                  { n: source.chunks_count, date: syncedAt(source) },
                  source.chunks_count
                )
              }}
            </span>
          </span>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0">
          <Button
            v-if="['text', 'faq'].includes(source.kind)"
            xs
            ghost
            slate
            icon="i-lucide-pencil"
            :title="t('LOCO.AI.KNOWLEDGE.EDIT')"
            @click="startEdit(source)"
          />
          <Button
            xs
            ghost
            slate
            icon="i-lucide-refresh-cw"
            :title="t('LOCO.AI.KNOWLEDGE.SYNC')"
            :disabled="isSyncing(source)"
            @click="sync(source)"
          />
          <Button
            xs
            ghost
            ruby
            icon="i-lucide-trash-2"
            :title="t('LOCO.AI.KNOWLEDGE.DELETE')"
            @click="remove(source)"
          />
        </div>
      </li>
    </ul>

    <Dialog
      ref="editDialogRef"
      :title="t('LOCO.AI.KNOWLEDGE.EDIT')"
      :confirm-button-label="t('LOCO.AI.KNOWLEDGE.SAVE')"
      @confirm="saveEdit"
    >
      <div v-if="editing" class="flex flex-col gap-3">
        <input
          v-model="editing.title"
          type="text"
          class="!mb-0"
          :placeholder="
            editing.kind === 'faq'
              ? t('LOCO.AI.KNOWLEDGE.QUESTION')
              : t('LOCO.AI.KNOWLEDGE.TITLE_OPTIONAL')
          "
        />
        <textarea
          v-model="editing.content"
          rows="6"
          class="!mb-0 resize-y"
          :placeholder="
            editing.kind === 'faq'
              ? t('LOCO.AI.KNOWLEDGE.ANSWER')
              : t('LOCO.AI.KNOWLEDGE.TEXT_PLACEHOLDER')
          "
        />
      </div>
    </Dialog>
  </div>
</template>
